import { z } from 'zod';
import type { EventType } from '../enums';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const text = (max: number) => z.string().trim().min(1).max(max);

/** Sprint 3 §5 — `events.data` per type (requirements §10.3). */
export const eventDataSchemas = {
  HEAT: z.object({
    signs: z.array(z.enum(['STANDING', 'MOUNTING', 'MUCUS', 'RESTLESS', 'MILK_DROP'])).default([]),
  }),
  SERVICE: z.object({
    method: z.enum(['AI', 'NATURAL']),
    technician: text(60).optional(),
    sireCode: text(40).optional(),
    straws: z.number().int().min(1).max(2).optional(),
  }),
  PREG_CHECK: z.object({
    result: z.enum(['PREGNANT', 'OPEN']),
    method: z.enum(['PALPATION', 'ULTRASOUND', 'OTHER']),
    checkedBy: text(60).optional(),
  }),
  CALVING: z.object({
    ease: z.enum(['NORMAL', 'ASSISTED', 'DIFFICULT']).optional(),
    placentaExpelled: z.boolean().optional(),
    calves: z
      .array(
        z.object({
          sex: z.enum(['FEMALE', 'MALE']),
          alive: z.boolean(),
          animalId: z.uuid().optional(), // required when alive
        }),
      )
      .max(2), // [] for opening calvings
  }),
  DRY_OFF: z.object({ reason: z.enum(['PLANNED', 'LOW_YIELD', 'HEALTH', 'OTHER']).optional() }),
  ABORTION: z.object({ monthsPregnant: z.number().int().min(1).max(9).optional() }),
  TREATMENT: z.object({
    problem: z.enum([
      'MASTITIS',
      'LAMENESS',
      'FEVER',
      'DIARRHEA',
      'BLOAT',
      'RETAINED_PLACENTA',
      'SKIN_TICKS',
      'EYE',
      'INJURY',
      'OTHER',
    ]),
    problemOther: text(80).optional(), // required when problem = OTHER
    drug: text(80),
    dose: text(40).optional(),
    route: z.enum(['INJECTION', 'ORAL', 'INTRAMAMMARY', 'TOPICAL', 'OTHER']).optional(),
    lastDate: isoDate.optional(), // ≥ event date
    milkWithdrawalDays: z.number().int().min(0).max(60),
    givenBy: text(60).optional(),
  }),
  VACCINATION: z.object({
    protocolId: z.uuid().optional(),
    product: text(80),
    dose: text(40).optional(),
    givenBy: text(60).optional(),
  }),
  DEWORMING: z.object({
    protocolId: z.uuid().optional(),
    product: text(80),
    dose: text(40).optional(),
    givenBy: text(60).optional(),
  }),
  WEIGHT: z.object({ kg: z.number().min(10).max(1200), method: z.enum(['SCALE', 'TAPE']) }),
  NOTE: z.object({ text: z.string().trim().min(1).max(1000) }),
} as const;

export type EventDataMap = { [K in EventType]: z.output<(typeof eventDataSchemas)[K]> };
export type HeatData = EventDataMap['HEAT'];
export type ServiceData = EventDataMap['SERVICE'];
export type PregCheckData = EventDataMap['PREG_CHECK'];
export type CalvingData = EventDataMap['CALVING'];
export type DryOffData = EventDataMap['DRY_OFF'];
export type AbortionData = EventDataMap['ABORTION'];
export type TreatmentData = EventDataMap['TREATMENT'];
export type VaccinationData = EventDataMap['VACCINATION'];
export type WeightData = EventDataMap['WEIGHT'];
export type NoteData = EventDataMap['NOTE'];

export const HEAT_SIGNS = ['STANDING', 'MOUNTING', 'MUCUS', 'RESTLESS', 'MILK_DROP'] as const;
export const TREATMENT_PROBLEMS = eventDataSchemas.TREATMENT.shape.problem.options;
export const TREATMENT_ROUTES = ['INJECTION', 'ORAL', 'INTRAMAMMARY', 'TOPICAL', 'OTHER'] as const;
export const PREG_CHECK_METHODS = ['PALPATION', 'ULTRASOUND', 'OTHER'] as const;
export const CALVING_EASE = ['NORMAL', 'ASSISTED', 'DIFFICULT'] as const;
export const DRY_OFF_REASONS = ['PLANNED', 'LOW_YIELD', 'HEALTH', 'OTHER'] as const;
export const SERVICE_METHODS = ['AI', 'NATURAL'] as const;
export const WEIGHT_METHODS = ['SCALE', 'TAPE'] as const;
